// Library Methods
import { useTranslation } from "react-i18next";
import React, { useMemo } from "react";

// Components
import { Box, Divider, Grid, Typography } from "@mui/material";
import ExamCardButton from "../ui/ExamCardButton";
import ExamCardToggles from "../ui/ExamCardToggles";
import WhichEyeSection from "../ui/WhichEyeSection";
import TitleWithTooltip from "../../../../../components/UI/TitleWithTooltip";
import ExamCardContainer from "../ui/ExamCardContainer";
import ExamCardHeader from "../ui/ExamCardHeader";

// Utilities
import {
  FixationLossMethodOptions,
  FixationPointShapeOptions,
} from "../../../../../utils/examReportsHelper";
import {
  gridLabels,
  GridTypes,
  StimulusColors,
  StimulusTypes,
} from "../../../../../enums";

const VisualFieldThresholdCard = ({
  label,
  examAlgorithm,
  selectedExamCards,
  updateExamCardProperty,
  onChangeEye,
  onChangeSectionOrder,
  onToggleCard,
}) => {
  // internationalization
  const { t } = useTranslation();

  const defaultVFThreshold = {
    examFieldType: examAlgorithm,
    examType: "FAST_THRESHOLD",
    eyeCheckboxes: { right: true, left: true },
    visualGrid: GridTypes.G24,
    stimulusSizeType: StimulusTypes.III,
    stimulusColor: StimulusColors.WHITE,
    fixationType: {
      fixationPointShapeType: FixationPointShapeOptions.Sphere,
      fixationLossMethod: FixationLossMethodOptions.EyeTracking,
    },
    sectionOrder: { right: 0, left: 1 },
    optionalToggles: {
      autoPause: true,
      calibration: true,
      encouragement: true,
      requiresTutorial: true,
    },
  };

  const selectedExamCard = selectedExamCards.find(
    (examCard) => examCard.examFieldType === examAlgorithm
  );
  const examCardInfo = selectedExamCard ?? defaultVFThreshold;
  const isActive = Boolean(selectedExamCard);

  const {
    examType,
    visualGrid,
    eyeCheckboxes,
    sectionOrder,
    optionalToggles,
    fixationType,
    stimulusSizeType,
    stimulusColor,
  } = examCardInfo;

  const vfThresholdMethods = useMemo(
    () => [
      { type: "FAST_THRESHOLD", label: t("word_fast") },
      { type: "STANDARD_THRESHOLD", label: t("word_standard") },
      { type: "FULL_THRESHOLD", label: t("word_full") },
    ],
    [t]
  );

  const vfThresholdGrids = useMemo(
    () => [
      { type: GridTypes.G24, label: gridLabels[GridTypes.G24] },
      { type: GridTypes.G24C, label: gridLabels[GridTypes.G24C] },
      { type: GridTypes.G30, label: gridLabels[GridTypes.G30] },
      { type: GridTypes.G10, label: gridLabels[GridTypes.G10] },
    ],
    []
  );

  const vfThresholdStimulusSizes = useMemo(
    () => [
      { type: StimulusTypes.III, label: StimulusTypes.III },
      { type: StimulusTypes.IV, label: StimulusTypes.IV },
      { type: StimulusTypes.V, label: StimulusTypes.V },
    ],
    []
  );

  const vfThresholdStimulusColors = useMemo(
    () => [
      { type: StimulusColors.WHITE, label: t("White") },
      { type: StimulusColors.RED, label: t("Red") },
    ],
    [t]
  );

  const textStyle = {
    color: isActive ? "#FFF" : "#5B6D7A",
    fontSize: "14px",
  };

  return (
    <ExamCardContainer isActive={isActive}>
      <ExamCardHeader
        label={label}
        isActive={isActive}
        onChange={(e) => {
          onToggleCard(e, defaultVFThreshold);
        }}
      />
      <Grid item container display="flex" flexDirection="column" gap="8px">
        {/* visual field method */}
        <Grid item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("word_method")}
            tooltip={t("method_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid container item display="flex" gap="8px">
            {vfThresholdMethods.map((vfMethod, index) => (
              <Grid item xs key={index}>
                <ExamCardButton
                  isSelected={vfMethod.type === examType}
                  updateValue={() =>
                    updateExamCardProperty("examType", vfMethod.type)
                  }
                  label={vfMethod.label}
                  isDisabled={!isActive}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* grid */}
        <Grid item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("word_grid")}
            tooltip={t("grid_size_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid item display="flex" gap="8px" height="32px">
            {vfThresholdGrids.map((vfGrid, index) => (
              <Grid item xs key={index}>
                <ExamCardButton
                  isSelected={vfGrid.type === visualGrid}
                  updateValue={() =>
                    updateExamCardProperty("visualGrid", vfGrid.type)
                  }
                  label={vfGrid.label}
                  isDisabled={!isActive}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* stimulus */}
        <Grid item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("report_stimulus_size")}
            tooltip={t("stimulus_size_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid item display="flex" gap="8px">
            {vfThresholdStimulusSizes.map((vfStimulusSize, index) => (
              <Grid item xs key={index}>
                <ExamCardButton
                  isSelected={vfStimulusSize.type === stimulusSizeType}
                  updateValue={() =>
                    updateExamCardProperty(
                      "stimulusSizeType",
                      vfStimulusSize.type
                    )
                  }
                  label={vfStimulusSize.label}
                  isDisabled={!isActive}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* stimulus color */}
        <Grid container item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("report_stimulus_color")}
            tooltip={t("stimulus_color_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid item display="flex" gap="8px">
            {vfThresholdStimulusColors.map((vfStimulusColor, index) => (
              <Grid item xs key={index}>
                <ExamCardButton
                  isSelected={vfStimulusColor.type === stimulusColor}
                  updateValue={() =>
                    updateExamCardProperty(
                      "stimulusColor",
                      vfStimulusColor.type
                    )
                  }
                  label={vfStimulusColor.label}
                  isDisabled={!isActive}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Fixation */}
        <Grid item display="flex" flexDirection="column" xs gap="8px">
          <TitleWithTooltip
            title={t("fixation")}
            tooltip={t("fixation_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid container item display="flex" gap="8px">
            <Grid item xs>
              <ExamCardButton
                isSelected={
                  FixationPointShapeOptions.Sphere ===
                  fixationType.fixationPointShapeType
                }
                updateValue={() =>
                  updateExamCardProperty("fixationType", {
                    ...examCardInfo.fixationType,
                    fixationPointShapeType: FixationPointShapeOptions.Sphere,
                  })
                }
                label={t("exams_modal_sphere")}
                isDisabled={!isActive}
              />
            </Grid>
            <Grid item xs>
              <ExamCardButton
                isSelected={
                  FixationPointShapeOptions.Diamond ===
                  fixationType.fixationPointShapeType
                }
                updateValue={() =>
                  updateExamCardProperty("fixationType", {
                    ...examCardInfo.fixationType,
                    fixationPointShapeType: FixationPointShapeOptions.Diamond,
                  })
                }
                label={t("diamond")}
                isDisabled={!isActive}
              />
            </Grid>
          </Grid>

          {/* Fixation loss */}
          <Grid item display="flex" flexDirection="column" xs gap="8px">
            <TitleWithTooltip
              title={t("fixation_loss_method")}
              tooltip={t("fixation_loss_method_tooltip")}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />

            <Grid container item display="flex" gap="8px" height="32px">
              <Grid item xs>
                <ExamCardButton
                  isSelected={
                    FixationLossMethodOptions.EyeTracking ===
                    fixationType.fixationLossMethod
                  }
                  updateValue={() =>
                    updateExamCardProperty("fixationType", {
                      ...examCardInfo.fixationType,
                      fixationLossMethod: FixationLossMethodOptions.EyeTracking,
                    })
                  }
                  label={t("eye_tracking")}
                  isDisabled={!isActive}
                />
              </Grid>
              <Grid item xs>
                <ExamCardButton
                  isSelected={
                    FixationLossMethodOptions.BlindSpot ===
                    fixationType.fixationLossMethod
                  }
                  updateValue={() =>
                    updateExamCardProperty("fixationType", {
                      ...examCardInfo.fixationType,
                      fixationLossMethod: FixationLossMethodOptions.BlindSpot,
                    })
                  }
                  label={t("word_blind_spot")}
                  isDisabled={!isActive}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <WhichEyeSection
          isActive={isActive}
          isRightFirst={sectionOrder.right === 0}
          eyeCheckboxes={eyeCheckboxes}
          onChangeSectionOrder={onChangeSectionOrder}
          onChangeEye={onChangeEye}
        />

        <Box height={{ xs: "0px", sm: "69px" }} />

        <Divider color="#5B6D7A" />

        <Grid display="flex" flexDirection="column">
          <ExamCardToggles
            isActive={isActive}
            optionalToggles={optionalToggles}
            updateExamCardProperty={updateExamCardProperty}
            showToggles={defaultVFThreshold.optionalToggles}
          />
        </Grid>

        <Grid item textAlign="center" visibility="hidden">
          <Typography color={isActive ? "#FFF" : "#5B6D7A"} fontSize="14px">
            {t("new_test")}
          </Typography>
          <Typography color={isActive ? "#FFF" : "#5B6D7A"} fontSize="14px">
            {t("try_it_out")}
          </Typography>
        </Grid>
      </Grid>
    </ExamCardContainer>
  );
};

export default VisualFieldThresholdCard;
